import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A component that updates accordion content and text blocks when variants change.
 * This listens for variant update events and dynamically updates:
 * - Accordion rows with variant metafields (Overview, Characteristics)
 * - Text blocks with product short description
 */
class VariantAccordionUpdater extends HTMLElement {
  #abortController = new AbortController();
  #isProcessing = false;
  #lastProcessedVariantId = null;

  connectedCallback() {
    // Find the section to attach event listener
    let closestSection = this.closest('.shopify-section');
    if (!closestSection) {
      closestSection = this.closest('.product-information');
    }
    if (!closestSection) {
      closestSection = document.querySelector('.shopify-section[data-section-type="product"]');
    }

    const { signal } = this.#abortController;

    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateContent, { signal });
    }

    // Also listen on document as backup
    document.addEventListener(ThemeEvents.variantUpdate, this.updateContent, { signal });

    // Set initial short-description block visibility (group_zqMHjM) on page load
    this.applyShortDescriptionBlockVisibilityFromDOM();
  }

  disconnectedCallback() {
    // Abort all pending operations and remove listeners
    this.#abortController.abort();
    this.#abortController = new AbortController();
    this.#isProcessing = false;
    this.#lastProcessedVariantId = null;
  }

  /**
   * Updates accordion and text content when variant changes.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateContent = (event) => {
    // Prevent multiple simultaneous processing
    if (this.#isProcessing) {
      return;
    }

    // Check if this is for the correct product
    if (event.detail.data.newProduct) {
      // Product changed, skip as this will trigger a full page reload
      return;
    }

    const variant = /** @type {any} */ (event.detail.resource);
    if (!variant || !variant.id) {
      return;
    }

    // Prevent processing the same variant twice
    if (this.#lastProcessedVariantId === variant.id) {
      return;
    }

    this.#isProcessing = true;
    this.#lastProcessedVariantId = variant.id;

    try {
      // Update accordion content - try both from HTML and from variant data
      this.updateAccordionContent(variant, event.detail.data.html);

      // Update variant short description text block
      this.updateVariantShortDescription(variant, event.detail.data.html);

      // Update text blocks with product short description
      this.updateProductDescription(event.detail.data.html);

      // Update Equipment accordion visibility and content
      this.updateEquipmentAccordion(variant, event.detail.data.html);

      // Reinitialize slideshow-controls after variant update (media gallery may be replaced)
      this.reinitializeSlideshowControls();
    } catch (error) {
      console.error('VariantAccordionUpdater: Error updating content', error);
    } finally {
      // Reset processing flag after a short delay to allow DOM updates to complete
      setTimeout(() => {
        this.#isProcessing = false;
      }, 100);
    }
  };

  /**
   * Updates accordion content for variant-specific metafields.
   * @param {any} variant - The variant object with metafields.
   * @param {Document} newHtml - The new HTML document from the fetch response.
   */
  updateAccordionContent(variant, newHtml) {
    // Update Overview accordion (variant_full_description)
    const overviewContent = /** @type {HTMLElement | null} */ (document.querySelector('.variant-accordion-content[data-accordion-type="overview"]'));

    if (overviewContent && overviewContent.isConnected) {
      // Check if element is inside slideshow to avoid breaking it
      if (overviewContent.closest('slideshow-controls, slideshow-component, slideshow-slide')) {
        return;
      }

      // Try to get from new HTML first
      const newOverviewContent = newHtml?.querySelector('.variant-accordion-content[data-accordion-type="overview"]');

      if (newOverviewContent) {
        const newContent = newOverviewContent.innerHTML;
        if (overviewContent.innerHTML !== newContent) {
          overviewContent.innerHTML = newContent;
        }
        if (variant.id) {
          overviewContent.dataset.variantId = String(variant.id);
        }
      } else if (variant.metafields?.custom?.variant_full_description) {
        // Fallback: update from variant metafield directly
        const metafieldValue = variant.metafields.custom.variant_full_description;
        const renderedValue = this.renderMetafield(metafieldValue);
        if (renderedValue && overviewContent.innerHTML !== renderedValue) {
          overviewContent.innerHTML = renderedValue;
        }
        if (variant.id) {
          overviewContent.dataset.variantId = String(variant.id);
        }
      }
    }

    // Update Characteristics accordion (variant_characteristics)
    const characteristicsContent = /** @type {HTMLElement | null} */ (document.querySelector('.variant-accordion-content[data-accordion-type="characteristics"]'));

    if (characteristicsContent && characteristicsContent.isConnected) {
      // Check if element is inside slideshow to avoid breaking it
      if (characteristicsContent.closest('slideshow-controls, slideshow-component, slideshow-slide')) {
        return;
      }

      const newCharacteristicsContent = newHtml?.querySelector('.variant-accordion-content[data-accordion-type="characteristics"]');

      if (newCharacteristicsContent) {
        const newContent = newCharacteristicsContent.innerHTML;
        if (characteristicsContent.innerHTML !== newContent) {
          characteristicsContent.innerHTML = newContent;
        }
        if (variant.id) {
          characteristicsContent.dataset.variantId = String(variant.id);
        }
      } else if (variant.metafields?.custom?.variant_characteristics) {
        // Fallback: update from variant metafield directly
        const metafieldValue = variant.metafields.custom.variant_characteristics;
        const renderedValue = typeof metafieldValue === 'string' ? metafieldValue : (metafieldValue?.value || '');
        if (renderedValue && characteristicsContent.innerHTML !== renderedValue) {
          characteristicsContent.innerHTML = renderedValue;
        }
        if (variant.id) {
          characteristicsContent.dataset.variantId = String(variant.id);
        }
      }
    }

    // Update Equipment accordion (variant_equipment)
    const equipmentContent = /** @type {HTMLElement | null} */ (document.querySelector('.variant-accordion-content[data-accordion-type="equipment"]'));

    if (equipmentContent && equipmentContent.isConnected) {
      // Check if element is inside slideshow to avoid breaking it
      if (equipmentContent.closest('slideshow-controls, slideshow-component, slideshow-slide')) {
        return;
      }

      // Try to get from new HTML first
      const newEquipmentContent = newHtml?.querySelector('.variant-accordion-content[data-accordion-type="equipment"]');

      if (newEquipmentContent) {
        const newContent = newEquipmentContent.innerHTML;
        if (equipmentContent.innerHTML !== newContent) {
          equipmentContent.innerHTML = newContent;
        }
        if (variant.id) {
          equipmentContent.dataset.variantId = String(variant.id);
        }
      } else if (variant.metafields?.custom?.variant_equipment) {
        // Fallback: update from variant metafield directly
        const metafieldValue = variant.metafields.custom.variant_equipment;
        const renderedValue = this.renderMetafield(metafieldValue);
        if (renderedValue && equipmentContent.innerHTML !== renderedValue) {
          equipmentContent.innerHTML = renderedValue;
        }
        if (variant.id) {
          equipmentContent.dataset.variantId = String(variant.id);
        }
      } else {
        // No data in new variant - clear the content
        if (equipmentContent.innerHTML.trim().length > 0) {
          equipmentContent.innerHTML = '';
        }
        if (variant.id) {
          equipmentContent.dataset.variantId = String(variant.id);
        }
      }
    }
  }

  /**
   * Updates Equipment accordion visibility when variant changes.
   * Shows/hides the accordion based on whether the variant has equipment data.
   * Content is already updated by updateAccordionContent, so this only manages visibility.
   * @param {any} variant - The variant object with metafields.
   * @param {Document} newHtml - The new HTML document from the fetch response.
   */
  updateEquipmentAccordion(variant, newHtml) {
    // Find the Equipment accordion content element
    const equipmentContent = /** @type {HTMLElement | null} */ (
      document.querySelector('.variant-accordion-content[data-accordion-type="equipment"]')
    );

    if (!equipmentContent || !equipmentContent.isConnected) {
      return;
    }

    // Check if element is inside slideshow to avoid breaking it
    if (equipmentContent.closest('slideshow-controls, slideshow-component, slideshow-slide')) {
      return;
    }

    // Find the parent accordion element (accordion-custom or details)
    const accordionElement = /** @type {HTMLElement | null} */ (
      equipmentContent.closest('accordion-custom') || equipmentContent.closest('details')
    );

    if (!accordionElement) {
      return;
    }

    // Check if new variant has equipment data
    // First check the content that was already updated by updateAccordionContent
    const currentContent = equipmentContent.innerHTML.trim();
    let hasEquipmentData = currentContent.length > 0;

    // If content is empty, check the new HTML or variant metafield
    if (!hasEquipmentData) {
      const newEquipmentContent = newHtml?.querySelector('.variant-accordion-content[data-accordion-type="equipment"]');

      if (newEquipmentContent) {
        hasEquipmentData = newEquipmentContent.innerHTML.trim().length > 0;
      } else if (variant.metafields?.custom?.variant_equipment) {
        // Fallback: check from variant metafield directly
        const metafieldValue = variant.metafields.custom.variant_equipment;
        const renderedValue = this.renderMetafield(metafieldValue);
        hasEquipmentData = renderedValue ? renderedValue.trim().length > 0 : false;
      }
    }

    // Show or hide the accordion based on whether there's data
    if (hasEquipmentData) {
      // Show the accordion
      accordionElement.style.display = '';
      accordionElement.removeAttribute('hidden');
    } else {
      // Hide the accordion
      accordionElement.style.display = 'none';
      accordionElement.setAttribute('hidden', '');
    }
  }

  /**
   * Applies short-description block wrapper visibility from current DOM (e.g. on page load).
   */
  applyShortDescriptionBlockVisibilityFromDOM() {
    const block = /** @type {HTMLElement | null} */ (document.querySelector('[data-variant-text-type="short-description"]'));
    if (!block) return;
    const contentEl = block.querySelector('span.metafield-multi_line_text_field')
      || block.querySelector('rte-formatter')
      || block.querySelector('p')
      || block.querySelector('div');
    const text = (contentEl && contentEl.textContent) ? contentEl.textContent.trim() : '';
    this.updateShortDescriptionBlockVisibility(block, text.length > 0);
  }

  /**
   * Shows or hides the short-description block wrapper (group_zqMHjM) based on whether the variant has content.
   * @param {HTMLElement} blockElement - The block with data-variant-text-type="short-description".
   * @param {boolean} hasContent - Whether the current variant has short description content.
   */
  updateShortDescriptionBlockVisibility(blockElement, hasContent) {
    const wrapper = blockElement && /** @type {HTMLElement | null} */ (blockElement.closest('[class*="zqMHjM"]'));
    if (!wrapper) return;
    if (hasContent) {
      wrapper.style.display = '';
    } else {
      wrapper.style.display = 'none';
    }
  }

  /**
   * Updates the variant short description text block.
   * @param {any} variant - The variant object with metafields.
   * @param {Document} newHtml - The new HTML document from the fetch response.
   */
  updateVariantShortDescription(variant, newHtml) {
    // Find the Product description block by data attribute
    let variantTextBlock = /** @type {HTMLElement | null} */ (
      document.querySelector('[data-variant-text-type="short-description"]')
    );

    // Fallback: find by block ID pattern (Qt8RmC) - can be with section prefix
    if (!variantTextBlock) {
      // Try to find any text block that contains Qt8RmC in its class
      const allTextBlocks = document.querySelectorAll('[class*="text-block--"]');
      for (const block of allTextBlocks) {
        if (block.className.includes('Qt8RmC')) {
          variantTextBlock = /** @type {HTMLElement} */ (block);
          break;
        }
      }
    }

    if (!variantTextBlock || !variantTextBlock.isConnected) {
      return;
    }

    // Find the inner content element
    // Structure: div.text-block > p > span.metafield-multi_line_text_field
    let contentElement = variantTextBlock.querySelector('span.metafield-multi_line_text_field');
    if (!contentElement) {
      contentElement = variantTextBlock.querySelector('rte-formatter');
    }
    if (!contentElement) {
      contentElement = variantTextBlock.querySelector('p');
    }
    if (!contentElement) {
      contentElement = variantTextBlock.querySelector('div, p');
    }
    if (!contentElement) {
      contentElement = variantTextBlock;
    }

    if (!contentElement || !contentElement.isConnected) {
      return;
    }

    // Try to get from new HTML first
    let newVariantTextBlock = newHtml?.querySelector('[data-variant-text-type="short-description"]');
    if (!newVariantTextBlock) {
      const newAllTextBlocks = newHtml?.querySelectorAll('[class*="text-block--"]');
      if (newAllTextBlocks) {
        for (const block of newAllTextBlocks) {
          if (block.className.includes('Qt8RmC')) {
            newVariantTextBlock = /** @type {HTMLElement} */ (block);
            break;
          }
        }
      }
    }

    let hasContent = false;

    if (newVariantTextBlock) {
      let newContentElement = newVariantTextBlock.querySelector('span.metafield-multi_line_text_field');
      if (!newContentElement) {
        newContentElement = newVariantTextBlock.querySelector('rte-formatter');
      }
      if (!newContentElement) {
        newContentElement = newVariantTextBlock.querySelector('p');
      }
      if (!newContentElement) {
        newContentElement = newVariantTextBlock.querySelector('div, p');
      }
      if (!newContentElement) {
        newContentElement = newVariantTextBlock;
      }

      if (newContentElement) {
        const currentText = contentElement.textContent || '';
        const newText = newContentElement.textContent || '';
        hasContent = (newText || '').trim().length > 0;

        if (currentText !== newText) {
          if (hasContent) {
            if (contentElement.tagName === 'SPAN' && newContentElement.tagName === 'SPAN') {
              contentElement.textContent = newContentElement.textContent;
            } else {
              contentElement.innerHTML = newContentElement.innerHTML;
            }
          } else {
            contentElement.textContent = '';
            if (contentElement.innerHTML !== '') contentElement.innerHTML = '';
          }
          if (variant.id) {
            variantTextBlock.dataset.variantId = String(variant.id);
          }
        }
      }
    }

    if (!hasContent) {
      hasContent = !!(variant.metafields?.custom?.variant_short_description);
    }

    // Fallback: update from variant metafield directly
    if (variant.metafields?.custom?.variant_short_description) {
      const metafieldValue = variant.metafields.custom.variant_short_description;
      const renderedValue = this.renderMetafield(metafieldValue);

      const textValue = typeof renderedValue === 'string'
        ? renderedValue.replace(/<[^>]*>/g, '').trim()
        : '';

      if (textValue) {
        hasContent = true;
        if (contentElement.tagName === 'SPAN') {
          contentElement.textContent = textValue;
        } else {
          const wrappedValue = `<p><span class="metafield-multi_line_text_field">${textValue}</span></p>`;
          if (contentElement.innerHTML !== wrappedValue) {
            contentElement.innerHTML = wrappedValue;
          }
        }
        if (variant.id) {
          variantTextBlock.dataset.variantId = String(variant.id);
        }
      }
    } else if (!hasContent) {
      contentElement.textContent = '';
      if (contentElement.innerHTML !== '') contentElement.innerHTML = '';
    }

    this.updateShortDescriptionBlockVisibility(variantTextBlock, hasContent);
  }

  /**
   * Updates product description text blocks.
   * @param {Document} newHtml - The new HTML document from the fetch response.
   */
  updateProductDescription(newHtml) {
    if (!newHtml) return;

    // Find all text blocks that might contain product_short_description
    // We look for text blocks in the product section
    const productSection = document.querySelector('.shopify-section[data-section-type="product"]');
    if (!productSection || !productSection.isConnected) return;

    // Find text blocks by their block ID (they have class text-block--{block.id})
    // Match text blocks from new HTML with current ones by their block ID
    const currentTextBlocks = productSection.querySelectorAll('[class*="text-block--"]');

    currentTextBlocks.forEach(currentBlock => {
      // Check if element is still in DOM
      if (!currentBlock.isConnected) return;

      // Skip if block is inside slideshow to avoid breaking it
      if (currentBlock.closest('slideshow-controls, slideshow-component, slideshow-slide')) {
        return;
      }

      // Extract block ID from class name (text-block--Qt8RmC)
      const blockIdMatch = currentBlock.className.match(/text-block--([\w-]+)/);
      if (!blockIdMatch) return;

      const blockId = blockIdMatch[1];

      // Find corresponding block in new HTML
      const newBlock = newHtml.querySelector(`[class*="text-block--${blockId}"]`);
      if (!newBlock) return;

      // Check if this block contains product_short_description by looking at its content
      // We'll update blocks that have changed and might be product description
      const currentContent = currentBlock.textContent || '';
      const newContent = newBlock.textContent || '';

      // Update if content changed (this will catch product_short_description updates)
      if (currentContent !== newContent) {
        // Find the inner content elements
        const currentContentElement = currentBlock.querySelector('rte-formatter, div, p') || currentBlock;
        const newContentElement = newBlock.querySelector('rte-formatter, div, p') || newBlock;

        if (currentContentElement && newContentElement && currentContentElement.isConnected) {
          // Only update if content actually differs
          if (currentContentElement.innerHTML !== newContentElement.innerHTML) {
            currentContentElement.innerHTML = newContentElement.innerHTML;
          }
        }
      }
    });
  }

  /**
   * Reinitializes slideshow-controls after variant update.
   * This is needed because media-gallery may be replaced by Shopify, breaking event listeners.
   */
  reinitializeSlideshowControls() {
    // Wait a bit for DOM to settle after media-gallery replacement
    setTimeout(() => {
      const slideshowControls = document.querySelector('slideshow-controls[ref="slideshowControls"]');
      if (!slideshowControls) return;

      const thumbnailsContainer = /** @type {HTMLElement | null} */ (
        slideshowControls.querySelector('.slideshow-controls__thumbnails-container')
      );
      const prevButton = /** @type {HTMLElement | null} */ (
        slideshowControls.querySelector('.thumbnail-nav-button--prev')
      );
      const nextButton = /** @type {HTMLElement | null} */ (
        slideshowControls.querySelector('.thumbnail-nav-button--next')
      );

      if (!thumbnailsContainer || !prevButton || !nextButton) return;

      // Get color scheme values before cloning
      // Try to get from button's existing style, or find color scheme element
      let backgroundColor = prevButton.style.getPropertyValue('--nav-button-bg')?.trim() || '';
      let foregroundColor = prevButton.style.getPropertyValue('--nav-button-fg')?.trim() || '';

      // If not found in inline styles, try to find color scheme element
      if (!backgroundColor || !foregroundColor) {
        // Find color scheme class from button or parent elements
        let colorSchemeClass = '';
        let element = prevButton;

        // Look for color scheme class in button or its parents
        while (element && !colorSchemeClass) {
          const match = element.className?.toString().match(/color-([\w-]+)/);
          if (match) {
            colorSchemeClass = `color-${match[1]}`;
            break;
          }
          const parent = element.parentElement;
          if (!parent) break;
          element = parent;
        }

        // If found, get colors from color scheme element
        if (colorSchemeClass) {
          const colorElement = document.querySelector(`.${colorSchemeClass}`);
          if (colorElement) {
            const computedStyle = getComputedStyle(colorElement);
            backgroundColor = computedStyle.getPropertyValue('--color-background').trim();
            foregroundColor = computedStyle.getPropertyValue('--color-foreground').trim();
          }
        }
      }

      // Remove existing event listeners by cloning and replacing
      // This ensures clean state
      const newPrevButton = /** @type {HTMLElement} */ (prevButton.cloneNode(true));
      const newNextButton = /** @type {HTMLElement} */ (nextButton.cloneNode(true));

      // Apply color scheme CSS variables to new buttons
      if (backgroundColor && foregroundColor) {
        [newPrevButton, newNextButton].forEach((button) => {
          button.style.setProperty('--nav-button-bg', backgroundColor);
          button.style.setProperty('--nav-button-fg', foregroundColor);
        });
      }

      prevButton.replaceWith(newPrevButton);
      nextButton.replaceWith(newNextButton);

      // Reinitialize navigation buttons
      function updateNavigationButtons() {
        if (!thumbnailsContainer) return;
        const scrollLeft = thumbnailsContainer.scrollLeft;
        const maxScrollLeft = thumbnailsContainer.scrollWidth - thumbnailsContainer.clientWidth;

        if (scrollLeft <= 0) {
          newPrevButton.style.display = 'none';
        } else {
          newPrevButton.style.display = 'flex';
        }

        if (scrollLeft >= maxScrollLeft - 1) {
          newNextButton.style.display = 'none';
        } else {
          newNextButton.style.display = 'flex';
        }
      }

      // Initial check
      updateNavigationButtons();

      // Re-add event listeners
      thumbnailsContainer.addEventListener('scroll', updateNavigationButtons);
      window.addEventListener('resize', updateNavigationButtons);

      newPrevButton.addEventListener('click', function () {
        if (!thumbnailsContainer) return;
        const scrollAmount = thumbnailsContainer.clientWidth * 0.8;
        thumbnailsContainer.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
      });

      newNextButton.addEventListener('click', function () {
        if (!thumbnailsContainer) return;
        const scrollAmount = thumbnailsContainer.clientWidth * 0.8;
        thumbnailsContainer.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      });
    }, 150);
  }

  /**
   * Renders a metafield value to HTML.
   * @param {any} metafield - The metafield value.
   * @returns {string} The rendered HTML.
   */
  renderMetafield(metafield) {
    if (typeof metafield === 'string') {
      return metafield;
    }

    if (!metafield || typeof metafield !== 'object') {
      return '';
    }

    // Handle metafield object
    if (metafield.value) {
      return String(metafield.value);
    }

    if (metafield.type === 'rich_text_field' && metafield.value) {
      return String(metafield.value);
    }

    return '';
  }
}

if (!customElements.get('variant-accordion-updater')) {
  customElements.define('variant-accordion-updater', VariantAccordionUpdater);
}
